import { toast } from '@aus-platform/design-system';
import { useRef } from 'react';
import useWebSocket from 'react-use-websocket';
import { ApiErrorType, handleRefreshToken } from 'shared/api';
import { setAIAuthCookie } from '../polygon-ai/ai-auth';

// Interfaces.
interface UseAppWebSocketOptions<TMessage> {
  url: string | null;
  enabled?: boolean;
  reconnectAttempts?: number;
  reconnectInterval?: number;
  onOpen?: VoidFunction;
  onMessage?: (data: TMessage) => void;
  onError?: (error?: unknown) => void;
  onClose?: VoidFunction;
}

interface AppWebSocketResult {
  sendJsonMessage: (payload: unknown) => void;
}

// Constants.
const DEFAULT_RECONNECT_ATTEMPTS = 0;
const DEFAULT_RECONNECT_INTERVAL_MS = 3000;

// Utils.
const isInvalidTokenMessage = (data: unknown): boolean => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'slug' in data &&
    (data as { slug?: string }).slug === ApiErrorType.InvalidToken
  );
};

export const useAppWebSocket = <TMessage>({
  url,
  enabled = true,
  reconnectAttempts = DEFAULT_RECONNECT_ATTEMPTS,
  reconnectInterval = DEFAULT_RECONNECT_INTERVAL_MS,
  onOpen,
  onClose,
  onError,
  onMessage,
}: UseAppWebSocketOptions<TMessage>): AppWebSocketResult => {
  // Refs.
  const isRefreshingRef = useRef<boolean>(false);

  // WebSocket.
  const { sendJsonMessage } = useWebSocket(enabled ? url : null, {
    share: true,
    reconnectAttempts,
    reconnectInterval,
    shouldReconnect: () => enabled && !isRefreshingRef.current,

    onOpen: () => {
      onOpen?.();
    },

    onClose: () => {
      onClose?.();
    },

    onError: (event) => {
      onError?.(event);
    },

    onMessage: async (event) => {
      try {
        const data = JSON.parse(event.data) as TMessage;

        if (!isInvalidTokenMessage(data)) {
          onMessage?.(data);
          return;
        }

        toast.error('Session expired, please try again.');

        if (isRefreshingRef.current) {
          return;
        }

        isRefreshingRef.current = true;

        try {
          const refreshed = await handleRefreshToken();

          if (!refreshed) {
            return;
          }

          setAIAuthCookie();
        } finally {
          isRefreshingRef.current = false;
        }
      } catch (error) {
        onError?.(error);
      }
    },
  });

  return {
    sendJsonMessage,
  };
};
